import { render } from "preact";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { api, forgetOfflineCopy, Unauthorized, type DraftSummary, type Folders, type Place, type ThreadSummary } from "./api";
import { AiPanelLazy, InboxAiLazy } from "./components/AiLazy";
import type { InboxAnalysis } from "./inbox-triage";
import { Compose } from "./components/Compose";
import { FolderMenuButton, folderRef, type FolderCommand, type FolderRef } from "./components/FolderMenu";
import { DraftList } from "./components/DraftList";
import { Icon } from "./components/icons";
import { Login } from "./components/Login";
import { Reader } from "./components/Reader";
import { SelectionBar, type BulkAction } from "./components/SelectionBar";
import { Settings } from "./components/Settings";
import { Sidebar } from "./components/Sidebar";
import { ThreadList } from "./components/ThreadList";
import { SYSTEM_NAMES } from "./format";
import { formatRoute, parseRoute, type Route } from "./route";
import "./styles.css";

interface Toast { text: string; undo?: () => Promise<void> }

// Message après une action. Il ne doit cacher aucun bouton :
// - par défaut, il flotte juste sous les en-têtes visibles (liste, lecture, réglages), dont
//   il mesure la hauteur à chaque affichage ;
// - tiroir des dossiers ouvert (téléphone), tout y est bouton : il se range alors en bas du
//   tiroir, dans le flux, et la liste des dossiers se raccourcit au lieu d'être recouverte.
function ToastBar({ toast, inline, onUndo }: { toast: Toast; inline: boolean; onUndo: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || inline) return;
    const bottoms = [...document.querySelectorAll<HTMLElement>(".list-head, .reader-head")]
      .map((h) => h.getBoundingClientRect())
      .filter((r) => r.width > 0 && r.height > 0 && r.top < innerHeight / 2)
      .map((r) => r.bottom);
    el.style.top = `${Math.max(0, ...bottoms) + 8}px`;
  }, [toast, inline]);
  return (
    <div ref={ref} class={`toast${inline ? " inline" : ""}`} role="status">
      <span>{toast.text}</span>
      {toast.undo && <button onClick={onUndo}>Annuler</button>}
    </div>
  );
}

function useRoute(): [Route, (r: Route) => void, (r: Route) => void] {
  const [route, setRoute] = useState(() => parseRoute(location.hash));
  useEffect(() => {
    const onHash = () => setRoute(parseRoute(location.hash));
    addEventListener("hashchange", onHash);
    return () => removeEventListener("hashchange", onHash);
  }, []);
  const go = useCallback((r: Route) => { location.hash = formatRoute(r); }, []);
  // Remplace l'étape courante de l'historique : après un envoi, « retour » ne rouvre pas
  // un brouillon qui n'existe plus.
  const replace = useCallback((r: Route) => { location.replace(formatRoute(r)); }, []);
  return [route, go, replace];
}

function Mail({ onSignedOut }: { onSignedOut: () => void }) {
  const [route, go, replace] = useRoute();
  const [folders, setFolders] = useState<Folders | null>(null);
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [next, setNext] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [query, setQuery] = useState(route.q ?? "");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [inboxAi, setInboxAi] = useState<"summary" | "cleanup" | null>(null);
  const [inboxAnalysis, setInboxAnalysis] = useState<InboxAnalysis | null>(null);

  const guard = (p: Promise<unknown>) => p.catch((e) => {
    if (e instanceof Unauthorized) onSignedOut();
    else setToast({ text: (e as Error).message });
  });

  const location_ = route.location ?? "inbox";
  const loadFolders = () => guard(api.folders().then(setFolders));
  const loadList = (append = false) => {
    setLoading(true);
    if (route.view !== "search" && location_ === "drafts") {
      return guard(api.drafts().then((r) => setDrafts(r.drafts))).finally(() => setLoading(false));
    }
    if (route.view === "search") {
      return guard(api.search(route.q ?? "").then(({ results }) => {
        setThreads(results.map((m) => ({
          id: m.threadId, count: 1, unread: m.read ? 0 : 1, starred: m.starred,
          hasAttachments: m.hasAttachments === true, lastAt: m.receivedAt, latest: m,
        })));
        setNext(null);
      })).finally(() => setLoading(false));
    }
    return guard(api.threads(location_, route.address, append ? next ?? undefined : undefined).then((page) => {
      setThreads((prev) => (append ? [...prev, ...page.threads] : page.threads));
      setNext(page.next);
    })).finally(() => setLoading(false));
  };

  useEffect(() => { loadFolders(); }, []);
  useEffect(() => {
    if (route.view === "settings") return;
    setPicked(new Set());
    setThreads([]);
    loadList();
    setDrawer(false);
  }, [route.view, route.location, route.address, route.q]);

  // Rafraîchit la liste quand l'onglet redevient visible (retour sur l'application).
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") { loadFolders(); loadList(); } };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  });

  const changed = (text: string, undo?: () => Promise<void>) => {
    loadFolders();
    loadList();
    if (text) setToast({ text, undo });
  };
  const undo = async () => {
    const u = toast?.undo;
    setToast(null);
    if (u) { await u(); changed(""); }
  };
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const newFolder = async () => {
    const name = prompt("Nom du nouveau dossier :")?.trim();
    if (!name) return;
    await guard(api.createFolder(name).then((f) => { loadFolders(); go({ view: "list", location: f.id }); }));
  };

  const newMessage = () => guard(api.createDraft({ kind: "new" }).then((d) => go({ ...route, view: "compose", draft: d.id, fresh: "1", thread: undefined })));
  const openCompose = (draftId: string, fresh = false) => go({ ...route, view: "compose", draft: draftId, fresh: fresh ? "1" : undefined });
  const closeCompose = () => {
    replace({ ...route, view: route.q ? "search" : "list", draft: undefined, fresh: undefined });
    loadFolders();
    if (location_ === "drafts") loadList();
  };
  const sent = (threadId: string) => {
    replace({ view: "list", location: location_ === "drafts" ? "sent" : location_, address: route.address, thread: threadId });
    loadFolders();
    loadList();
  };

  const pick = (id: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  // Échap annule la sélection.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPicked(new Set()); };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, []);

  // Actions sur la sélection. Les résultats de recherche viennent de plusieurs dossiers :
  // chaque conversation est traitée depuis le sien.
  const bulk = async (action: BulkAction) => {
    const byFolder = new Map<string, string[]>();
    for (const t of threads) {
      if (!picked.has(t.id)) continue;
      const from = route.view === "search" ? t.latest.location : location_;
      byFolder.set(from, [...(byFolder.get(from) ?? []), t.id]);
    }
    const n = picked.size;
    const plural = n > 1 ? "s" : "";
    try {
      if (action.kind === "forever") {
        if (!confirm(`Supprimer définitivement ${n} conversation${plural} ? Cette action est irréversible.`)) return;
        await api.deleteThreads([...picked]);
        setPicked(new Set());
        changed(`${n} conversation${plural} supprimée${plural} définitivement`);
        return;
      }
      const change = action.kind === "move" ? { location: action.to } : { read: action.read };
      const done = await Promise.all([...byFolder].map(([from, ids]) => api.updateThreads(ids, from, change).then((r) => ({ from, ids: r.ids }))));
      setPicked(new Set());
      if (action.kind === "read") {
        changed(`${n} conversation${plural} marquée${plural} comme ${action.read ? "lue" : "non lue"}${plural}`);
        return;
      }
      if (route.thread && picked.has(route.thread)) go({ ...route, thread: undefined });
      // « archivée », « mise à la corbeille »… : le premier mot s'accorde.
      const label = action.label.replace(/^\S+/, (w) => w + plural);
      changed(`${n} conversation${plural} ${label}`, async () => {
        for (const { from, ids } of done) if (ids.length) await api.update(ids, { location: from });
      });
    } catch (e) {
      if (e instanceof Unauthorized) onSignedOut();
      else setToast({ text: (e as Error).message });
    }
  };

  const emptyTrash = async () => {
    const total = folders?.system.trash?.total ?? 0;
    const what = total === 1 ? "Son e-mail sera supprimé" : `Ses ${total || ""} e-mails seront supprimés`.replace("  ", " ");
    if (!confirm(`Vider la corbeille ? ${what} définitivement.`)) return;
    let removed = 0;
    await guard((async () => {
      // Par lots, tant qu'il en reste.
      for (;;) {
        const r = await api.emptyTrash();
        removed += r.deleted;
        if (r.remaining === 0 || r.deleted === 0) break;
      }
    })());
    if (route.thread) go({ ...route, thread: undefined });
    changed(`Corbeille vidée (${removed} e-mail${removed > 1 ? "s" : ""})`);
  };

  // Menu « ⋯ » d'un dossier (barre latérale ou en-tête de la liste).
  const viewing = (f: FolderRef) => route.view === "list" && location_ === f.location && route.address === f.address;
  const setPlace = (f: FolderRef, place: Place) =>
    f.kind === "address" ? api.setAddressPlace(f.address!, place) : api.setFolderPlace(f.location, place);
  const folderCommand = async (f: FolderRef, command: FolderCommand) => {
    const where = { location: f.location, address: f.address };
    const s = (n: number) => (n > 1 ? "s" : "");
    try {
      switch (command.kind) {
        case "read": {
          const { updated } = await api.updateFolder(where, { read: true });
          changed(updated ? `« ${f.label} » : ${updated} e-mail${s(updated)} marqué${s(updated)} comme lu${s(updated)}` : "Tout était déjà lu");
          break;
        }
        case "move": {
          const what = f.total === 1 ? "l'e-mail" : `les ${f.total} e-mails`;
          const where_ = command.to === "trash" ? "à la corbeille (ils y restent 30 jours avant d'être supprimés)" : "dans les archives";
          if (!confirm(`Mettre ${what} de « ${f.label} » ${where_} ?`)) return;
          const { updated } = await api.updateFolder(where, { to: command.to });
          if (viewing(f) && route.thread) go({ ...route, thread: undefined });
          changed(`${updated} e-mail${s(updated)} ${command.to === "trash" ? "mis à la corbeille" : `archivé${s(updated)}`}`);
          break;
        }
        case "empty":
          await emptyTrash();
          break;
        case "place": {
          const before = f.place ?? "normal";
          await setPlace(f, command.place);
          // Masquer ou réafficher une adresse change aussi « Tous les e-mails ».
          loadFolders();
          loadList();
          const text = command.place === "pinned" ? `« ${f.label} » épinglé en haut`
            : command.place === "hidden" ? `« ${f.label} » masqué : plus de notification${f.kind === "address" ? " ni d'e-mail dans « Tous les e-mails »" : ""}`
            : `« ${f.label} » remis dans la liste`;
          setToast({ text, undo: async () => { await setPlace(f, before); } });
          break;
        }
        case "notify":
          await api.setNotify(f.address!, command.notify);
          loadFolders();
          setToast({ text: command.notify ? `Notifications activées pour « ${f.label} »` : `Plus de notification pour « ${f.label} »` });
          break;
        case "rename": {
          const name = prompt("Nouveau nom du dossier :", f.label)?.trim();
          if (!name || name === f.label) return;
          await api.renameFolder(f.location, name);
          loadFolders();
          break;
        }
        case "delete":
          if (!confirm(`Supprimer le dossier « ${f.label} » ? Ses e-mails retourneront dans la boîte de réception.`)) return;
          await api.deleteFolder(f.location);
          if (viewing(f)) go({ view: "list", location: "inbox" });
          changed(`Dossier « ${f.label} » supprimé`);
          break;
      }
    } catch (e) {
      if (e instanceof Unauthorized) onSignedOut();
      else setToast({ text: (e as Error).message });
    }
  };

  const custom = folders?.custom.find((f) => f.id === location_);
  const current = route.view === "list" ? folderRef(folders, location_, route.address) : null;
  const title = route.view === "search" ? `Recherche : ${route.q}`
    : route.address ? route.address
    : custom?.name ?? SYSTEM_NAMES[location_] ?? "Dossier";
  const reading = route.view === "settings" || route.view === "compose" || Boolean(route.thread) || Boolean(route.ai);
  const back = () => go({ ...route, thread: undefined, ai: undefined, view: route.view === "settings" ? "list" : route.view });

  return (
    <div class={`layout${drawer ? " drawer-open" : ""}${reading ? " reading" : ""}`}>
      <div class="offline-banner" role="status">Hors ligne : affichage des e-mails déjà chargés</div>
      <Sidebar folders={folders} route={route} go={(r) => { setQuery(""); go(r); }} onNewFolder={newFolder} onNewMessage={() => { setDrawer(false); newMessage(); }}
        onFolderCommand={folderCommand} footer={toast && drawer ? <ToastBar toast={toast} inline onUndo={undo} /> : null} />
      {drawer && <div class="scrim" onClick={() => setDrawer(false)} />}

      <section class="list">
        {picked.size > 0 ? (
          <SelectionBar count={picked.size} total={threads.length} here={route.view === "search" ? null : location_} folders={folders}
            onAll={() => setPicked(new Set(threads.map((t) => t.id)))} onClear={() => setPicked(new Set())} onAction={bulk} />
        ) : (
        <div class="list-head">
          <button class="icon-btn only-mobile" aria-label="Dossiers" onClick={() => setDrawer(true)}><Icon name="menu" /></button>
          <h2>{title}</h2>
          {location_ === "trash" && route.view !== "search" && threads.length > 0 && (
            <button class="btn danger" onClick={emptyTrash}>Vider la corbeille</button>
          )}
          {current && current.location !== "trash" && <FolderMenuButton folder={current} onCommand={folderCommand} />}
        </div>
        )}
        {route.view === "list" && location_ === "inbox" && !route.address && <div class="inbox-ai-buttons">
          <button type="button" class="chip" onClick={() => setInboxAi("summary")}><Icon name="spark" size={16} />Résumé IA</button>
          <button type="button" class="chip" onClick={() => setInboxAi("cleanup")}><Icon name="trash" size={16} />Cleanup IA</button>
        </div>}
        <form class="search" role="search" onSubmit={(e) => { e.preventDefault(); if (query.trim()) go({ view: "search", q: query.trim() }); }}>
          <input type="search" placeholder="Rechercher dans les e-mails" aria-label="Rechercher"
            value={query} onInput={(e) => setQuery((e.target as HTMLInputElement).value)} />
        </form>
        {location_ === "drafts" && route.view !== "search" ? (
          <DraftList drafts={drafts} selected={route.draft} loading={loading} onOpen={(d) => openCompose(d.id)} />
        ) : (
          <ThreadList
            threads={threads} selected={route.thread} loading={loading} hasMore={next !== null}
            onMore={() => loadList(true)} sent={location_ === "sent" && route.view !== "search"}
            picked={picked} onPick={pick}
            onOpen={(t) => go({ ...route, view: route.view === "compose" ? "list" : route.view, draft: undefined, ai: undefined, thread: t.id, location: route.view === "search" ? t.latest.location : route.location })}
            emptyText={route.view === "search" ? "Aucun résultat." : "Aucun e-mail dans ce dossier."}
          />
        )}
        <button class="fab only-mobile" aria-label="Nouveau message" onClick={newMessage}><Icon name="pen" /></button>
      </section>

      {route.view === "settings" ? (
        <Settings onBack={back} onLogout={onSignedOut} notify={(text) => changed(text)} />
      ) : route.view === "compose" && route.draft ? (
        <Compose key={route.draft} draftId={route.draft} fresh={route.fresh === "1"} folders={folders} onClose={closeCompose} onSent={sent}
          onSettings={() => go({ view: "settings" })}
          notify={(text) => setToast({ text })} />
      ) : route.thread && route.ai ? (
        <AiPanelLazy key={route.ai} messageId={route.ai} threadId={route.thread} location={location_}
          onBack={() => go({ ...route, ai: undefined })} onDraft={(id) => openCompose(id)}
          onSettings={() => go({ view: "settings" })} notify={(text) => setToast({ text })} />
      ) : route.thread ? (
        <Reader threadId={route.thread} location={location_} folders={folders} onBack={back} onChanged={changed} onCompose={(id) => openCompose(id, true)}
          onAsk={(id) => go({ ...route, ai: id })} onSettings={() => go({ view: "settings" })} />
      ) : (
        <section class="reader"><div class="placeholder">Sélectionnez une conversation.</div></section>
      )}

      {toast && !drawer && <ToastBar toast={toast} inline={false} onUndo={undo} />}
      {inboxAi && <InboxAiLazy view={inboxAi} onView={setInboxAi} analysis={inboxAnalysis} onAnalysis={setInboxAnalysis}
        onClose={() => setInboxAi(null)} onChanged={changed}
        onSettings={() => { setInboxAi(null); go({ view: "settings" }); }}
        onOpen={(message) => { setInboxAi(null); go({ view: "list", location: "inbox", thread: message.threadId }); }} />}
    </div>
  );
}

function App() {
  const [me, setMe] = useState<{ authenticated: boolean; passkeys: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const check = () => {
    setError(null);
    return api.me().then(setMe).catch((e) => setError((e as Error).message));
  };
  useEffect(() => {
    check();
    // Le réseau revient : nouvel essai sans attendre que Robin appuie sur « Réessayer ».
    addEventListener("online", check);
    return () => removeEventListener("online", check);
  }, []);
  useEffect(() => { if (me && !me.authenticated) forgetOfflineCopy(); }, [me]);
  if (!me) {
    return (
      <div class="login">
        <div class="boot">
          {error ? <><p>{error}</p><button class="btn" onClick={check}>Réessayer</button></> : <p>Chargement…</p>}
        </div>
      </div>
    );
  }
  if (!me.authenticated) return <Login passkeys={me.passkeys} onDone={check} />;
  return <Mail onSignedOut={() => setMe({ ...me, authenticated: false })} />;
}

render(<App />, document.getElementById("app")!);

// Rend la web app installable et lui permet de démarrer sans attendre le réseau.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  navigator.serviceWorker.register("/sw.js").catch(() => {});
}
